import { useState } from "react";
import { Loader2, MessageSquarePlus, Trash2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { spaceOf } from "@/lib/spaces";
import { fmtDate, fromNow } from "@/lib/time";
import { SpaceAvatar } from "@/components/SpaceAvatar";

const UpdateForm = ({ rid }) => {
  const { run } = useApp();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const post = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    const ok = await run(() => api.post(`/reminders/${rid}/updates`, { body: text }), "Update added");
    setBusy(false);
    if (ok) setText("");
  };
  return (
    <form onSubmit={post} className="rounded-xl border bg-background/40 p-3" data-testid="ticket-update-form">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add an update… (Ctrl+Enter to post)" data-testid="ticket-update-input"
        onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && post(e)} className="border-0 bg-transparent p-1 focus-visible:ring-0" />
      <div className="mt-2 flex justify-end">
        <button type="submit" disabled={busy || !text.trim()} data-testid="ticket-update-submit" className="flex h-9 items-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />} Post update
        </button>
      </div>
    </form>
  );
};

export const TicketUpdates = ({ r }) => {
  const { run } = useApp();
  const updates = [...(r.updates || [])].reverse();
  const remove = (uid) => run(() => api.delete(`/reminders/${r.id}/updates/${uid}`), "Update removed");
  return (
    <section className="space-y-4">
      <UpdateForm rid={r.id} />
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Updates · {updates.length}</p>
      {updates.length === 0 && <p className="text-sm text-muted-foreground" data-testid="ticket-no-updates">No updates yet.</p>}
      <ol className="relative space-y-4 border-l border-white/10 pl-5" data-testid="ticket-updates-list">
        {updates.map((u) => (
          <li key={u.id} className="group relative" data-testid={`ticket-update-${u.id}`}>
            <span className="absolute -left-[27px] top-0.5"><SpaceAvatar space={u.created_by} /></span>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">{spaceOf(u.created_by).name}</span>
              <span title={fmtDate(u.created_at)}>{fromNow(u.created_at)}</span>
              <button onClick={() => remove(u.id)} data-testid={`ticket-update-delete-${u.id}`} title="Remove update" className="ml-auto rounded p-1 opacity-0 transition-opacity hover:text-red-400 group-hover:opacity-100">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm">{u.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
};
